import { CodeReferenceBlockConfigurationRecord } from "models/view-models/block-configurations/code-reference-block-configuration-record";
import { BlockRecord } from "models/view-models/block-record";
import { CodeReferenceBlockHighlightRecord } from "models/view-models/code-reference-block-highlights/code-reference-block-highlight-record";
import CodeReferenceBlockHighlightService from "utilities/services/code-reference-block-highlights/code-reference-block-highlights-service";
import usePageBlockError from "../pages/use-page-block-errors";
import { RecordUtils } from "@rsm-hcd/javascript-core";

export interface HighlightApiParams {
    block: BlockRecord;
    handleFinished: (block?: BlockRecord) => void;
    handleSaving?: () => void;
}

export const useCodeReferenceHighlightsApi = ({
    block,
    handleFinished,
    handleSaving,
}: HighlightApiParams) => {
    const { create: createApi } =
        CodeReferenceBlockHighlightService.useCreate();
    const { update: updateApi } =
        CodeReferenceBlockHighlightService.useUpdate();
    const { delete: deleteApi } =
        CodeReferenceBlockHighlightService.useDelete();

    const { showError } = usePageBlockError();

    const createHighlight = async (
        highlightRecord: CodeReferenceBlockHighlightRecord
    ): Promise<void> => {
        if (!highlightRecord || !block.configuration.id) {
            showError("creating", "highlight");
            return;
        }

        handleSaving?.();

        try {
            const highlight = highlightRecord.with({
                configurationId: block.configuration.id,
            });
            const response = await createApi(highlight);

            if (response.resultObject == null) {
                throw new Error(
                    `No response when creating code reference highlight for configuration: ${highlightRecord.configurationId}`
                );
            }

            const newHighlight = response.resultObject;

            const updatedBlock = updateBlock(newHighlight, block, "create");
            handleFinished(updatedBlock);

            return;
        } catch (error) {
            showError("creating", "highlight");
            console.error(error);
            handleFinished();
            return;
        }
    };

    const updateHighlight = async (
        highlightRecord: CodeReferenceBlockHighlightRecord
    ) => {
        if (!highlightRecord.id) {
            showError("updating", "highlight");
            return;
        }

        handleSaving?.();

        try {
            const response = await updateApi(
                {
                    highlightColor: highlightRecord.highlightColor,
                    note: highlightRecord.note,
                },
                { id: highlightRecord.id }
            );

            if (response.resultObject == null) {
                throw new Error(
                    `No response when updating code reference highlight: ${highlightRecord.id}`
                );
            }

            const updatedHighlight = response.resultObject;
            const updatedBlock = updateBlock(updatedHighlight, block, "update");
            handleFinished(updatedBlock);

            return;
        } catch (error) {
            showError("updating", "highlight");
            console.error(error);
            handleFinished();
            return;
        }
    };

    const deleteHighlight = async (highlightId: number | undefined) => {
        if (!highlightId) {
            showError("deleting", "highlight");
            return;
        }

        handleSaving?.();

        try {
            await deleteApi(highlightId);

            const updatedBlock = updateBlock(
                new CodeReferenceBlockHighlightRecord({ id: highlightId }),
                block,
                "delete"
            );
            handleFinished(updatedBlock);
        } catch (error) {
            showError("deleting", "highlight");
            console.error(error);
            handleFinished();
            return;
        }
    };

    return { createHighlight, updateHighlight, deleteHighlight };
};

const updateBlock = (
    newHighlight: CodeReferenceBlockHighlightRecord,
    block: BlockRecord,
    updateType: "create" | "update" | "delete"
) => {
    const configuration = RecordUtils.ensureRecord(
        block.configuration,
        CodeReferenceBlockConfigurationRecord
    );

    const highlights = [...(configuration.highlights ?? [])];

    const index = highlights.findIndex((h) => h.id === newHighlight.id);

    if (index !== -1) {
        if (updateType === "delete") {
            highlights.splice(index, 1);
        } else {
            // replace existing highlight if it exists
            highlights[index] = newHighlight;
        }
    } else {
        // otherwise, add new highlight
        highlights.push(newHighlight);
    }

    return block.with({
        configuration: configuration.with({ highlights }),
    });
};
